import { RcsComponent, RcsUseCase } from '../../../components/marketing/templates/rcs/rcs-types';

export type Channel = 'whatsapp' | 'sms' | 'email' | 'rcs';
export type Category = 'Marketing' | 'Authentication' | 'Utility';
export type Language = 'English (US)' | 'English (UK)' | 'Spanish' | 'French';
export type HeaderType = 'none' | 'text' | 'media';
export type MediaType = 'image' | 'video' | 'document';
export type ButtonType = 'none' | 'call_to_action' | 'quick_reply';

export interface QuickReplyButton { id: string; label: string; }

// Real Meta WhatsApp template button types (verified 2026-08-18): URL (Visit
// Website — static or dynamic with a trailing {{1}}), PHONE_NUMBER (Call
// Phone Number), VOICE_CALL (Call on WhatsApp — a native WhatsApp voice call,
// distinct from PHONE_NUMBER which dials a regular number), COPY_CODE (Copy
// Offer Code). Meta's stated cap: up to 2 URL buttons, 1 phone number, 1
// copy-code button (4 total) — VOICE_CALL treated as max 1 too here.
export type CtaButtonType = 'URL' | 'PHONE_NUMBER' | 'VOICE_CALL' | 'COPY_CODE';
export interface CtaButton {
  id: string;
  type: CtaButtonType;
  text: string;
  url?: string;
  phoneNumber?: string;
  // Only meaningful when type === 'URL'. Dynamic means the url ends in a
  // {{1}} placeholder and needs a full example URL (not just the variable
  // value) for template review.
  urlType?: 'static' | 'dynamic';
  // Dynamic URL's example (full URL, {{1}} resolved to a sample) or
  // COPY_CODE's example offer code — same field, different meaning per type.
  example?: string;
}

export interface MessageVariable {
  index: number;
  name: string;
  example: string;
}

export interface TemplateFormData {
  channel: Channel;
  templateName: string;
  whatsappBusinessAccount: string;
  category: Category;
  language: Language;
  headerType: HeaderType;
  headerText: string;
  mediaType: MediaType;
  mediaFile: File | null;
  mediaFileName: string;
  messageBody: string;
  buttonType: ButtonType;
  quickReplyButtons: QuickReplyButton[];
  ctaButtons: CtaButton[];
  footer: string;
  headerImage: string;
  headerVideo: string; // Add this
  headerDocument: string; // Add this
  messageVariables: MessageVariable[];
  shortenUrls?: boolean;
  emailSubject?: string;
  emailHtmlContent?: string;
  emailPlainText?: string;
  emailPreviewText?: string;
  emailSenderEmail?: string;
  emailSenderName?: string;
  emailVariables?: { name: string; defaultValue: string }[];
  // Unlayer design JSON — null once the Plain Text tab is the latest edit.
  emailDesign?: object | null;

  // RCS-specific
  rcsAgentId?: string;
  rcsUseCase?: RcsUseCase;
  rcsComponents?: RcsComponent[];
  rcsFallbackChannel?: 'sms' | 'none';
}

export const defaultFormData: TemplateFormData = {
  channel: 'whatsapp',
  templateName: '',
  whatsappBusinessAccount: '',
  category: 'Marketing',
  language: 'English (US)',
  headerType: 'none',
  headerText: '',
  mediaType: 'image',
  mediaFile: null,
  mediaFileName: '',
  messageBody: '',
  buttonType: 'none',
  quickReplyButtons: [
    { id: '1', label: 'Yes' },
    { id: '2', label: 'No' },
    { id: '3', label: 'Maybe' },
  ],
  ctaButtons: [
    { id: '1', type: 'URL', text: '', url: '' },
  ],
  footer: '',
  headerImage: '',
  headerVideo: '', // Add this
  headerDocument: '', // Add this
  messageVariables: [],
};

export interface TemplateCreatorProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSaveDraft?: (data: TemplateFormData) => void;
  onSendRequest?: (data: TemplateFormData) => void;
  templates?: TemplateModule[];
  wabaAccounts?: Array<{ value: string; label: string }>;
  editingTemplate?: TemplateModule | null;
  isCopyMode?: boolean;
  selectedTemplate?: TemplateModule | null;
   initialChannel?: Channel;
  // When rendered inside a parent drawer (e.g. ReuseableDrawer on the
  // templates list page), skip the component's own fixed-overlay modal
  // wrapper so it doesn't double up with the parent's positioning.
  asDrawer?: boolean;
}


export interface TemplateModule {
  id: string;
  shortened_url?: string;
  shorten_url?: string;
  snapshot_url?: string | null;
  extracted_url?: string[];
  template_name: string;
  subject?: string;
  content: string;
  variables: {
    name: string;
    index: number;
    example: string;
  }[];
  header_content?: string;
  header_type?: "image" | "text" | null;
  header_text?: string | null;
  footer?: string | null;
  buttons?: any;
  language: string;
  category: string;
  wa_status: string;
  channel: string;
  // false = disabled: hidden from campaign template pickers and rejected (400)
  // if a campaign uses it. Missing on older records — treat as enabled.
  is_active?: boolean;
  is_global?: boolean;
  waba_id?: string;
  html_content?: string | null;
  wa_template_id?: string;
  org_id?: string;
  button_type?: "call_to_action" | "quick_reply" | null;
  media_file_name?: string | null;
  media_type?: "image" | "video" | "document" | null;
  quick_reply_buttons?: Array<{ id: string; label: string }>;
  sample_values?: Record<number, string>;
}